import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { PopoverDirective } from '@epicuro-next/ataraxia/popover';
import {
  ConnectDeclineReason,
  IConnectContact,
} from '@epicuro-next/data/connect';
import { CONNECT_VISIBLE_AVATARS } from '@epicuro-next/platform/configuration';
import {
  acceptIncomingCall,
  declineIncomingCall,
  listenSocketCallDeclineMessage,
  listenSocketOnCancelVideoCall,
  selectIncomingCall,
  selectUserContactsState,
  showCallNotification,
} from '@epicuro-next/state/connect-state';
import { setUserMenuOpenedState } from '@epicuro-next/state/user-ui-state';
import { select, Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

interface UserContactsData {
  avatars: IConnectContact[];
  total: number;
  showContactsNumber: boolean;
  contactsNumber: number;
}

@Component({
  selector: 'epc-user-contacts',
  templateUrl: './user-contacts.component.html',
  styleUrls: ['./user-contacts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserContactsComponent implements OnInit, OnDestroy {
  @ViewChild(PopoverDirective, { static: false })
  private pop: PopoverDirective;

  @ViewChild('audioRef')
  public audioRef: ElementRef<HTMLAudioElement>;

  constructor(private store: Store) {}

  public contactsData$: Observable<UserContactsData>;
  public contactCalling$ = this.store.pipe(
    select(selectIncomingCall),
    map((call) => call?.contact),
  );
  private destroy$ = new Subject<void>();

  public ngOnInit() {
    this.subscribeConnectEnabled();
    this.subscribeContactCalling();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public acceptCall() {
    this.store.dispatch(acceptIncomingCall());
    this.pop.close();
  }

  public declineCall(reason: ConnectDeclineReason) {
    this.store.dispatch(declineIncomingCall({ reason }));
    this.pop.close();
  }

  private subscribeConnectEnabled() {
    this.contactsData$ = this.store.pipe(
      select(selectUserContactsState),
      shareReplay({ bufferSize: 1, refCount: true }),
      switchMap((contacts: IConnectContact[]) => {
        const avatars = contacts.slice(0, CONNECT_VISIBLE_AVATARS);
        return of({
          avatars,
          total: contacts.length,
          showContactsNumber: contacts.length > avatars.length,
          contactsNumber: contacts.length - CONNECT_VISIBLE_AVATARS,
        });
      }),
      takeUntil(this.destroy$),
    );
  }

  private subscribeContactCalling() {
    this.store
      .pipe(select(selectIncomingCall), takeUntil(this.destroy$))
      .subscribe((incomingCall) => {
        if (incomingCall) {
          this.pop.open();
          this.store.dispatch(listenSocketOnCancelVideoCall());
          this.store.dispatch(listenSocketCallDeclineMessage());
          this.store.dispatch(showCallNotification());
        } else if (incomingCall === null) {
          this.pop.close();
        }
      });
  }

  public openUserMenu() {
    this.store.dispatch(setUserMenuOpenedState({ opened: true }));
  }
}
